import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material';
import { BugReport, CheckCircle, Close } from '@mui/icons-material';
import { getErrorInfo, type ErrorInfo } from '../services/api';
import { reportError } from '../services/errorReporting';

interface ErrorMessageProps {
  error: Error | unknown;
  userFriendlyMessage: string;
  onClose?: () => void;
}

export default function ErrorMessage({ error, userFriendlyMessage, onClose }: ErrorMessageProps) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const errorInfo: ErrorInfo = getErrorInfo(error);

  const handleReport = async () => {
    setSending(true);
    try {
      await reportError(errorInfo);
      setSent(true);
      setTimeout(() => {
        setOpen(false);
        setSent(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to send error report:', err);
    } finally {
      setSending(false);
    }
  };

  const getUserFriendlyError = (errorInfo: ErrorInfo): string => {
    // Network connectivity issues
    if (errorInfo.code === 'NETWORK_ERROR' || errorInfo.message.includes('Network Error')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    // 404 errors - API endpoint not found (likely configuration issue)
    if (errorInfo.code === 'NOT_FOUND' || errorInfo.message.includes('404') || errorInfo.message.includes('not found')) {
      return 'We\'re having trouble connecting to our services. Our team has been notified and is working on a fix. Please try again in a few minutes.';
    }

    // Authentication errors
    if (errorInfo.code === 'INVALID_CREDENTIALS') {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (errorInfo.code === 'UNAUTHORIZED') {
      return 'Your session has expired. Please log in again.';
    }

    // Timeout errors
    if (errorInfo.message.includes('timeout')) {
      return 'The request took too long to complete. Please try again.';
    }

    // Server errors (5xx)
    if (errorInfo.code.startsWith('5') || errorInfo.message.includes('500') || errorInfo.message.includes('502') || errorInfo.message.includes('503')) {
      return 'Our servers are experiencing issues. Our team has been notified. Please try again in a few minutes.';
    }

    return userFriendlyMessage;
  };

  return (
    <>
      {/* Custom Error Box - No MUI Alert */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          border: '2px solid',
          borderColor: '#DC2626',
          borderRadius: 1,
          bgcolor: '#FFFFFF',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="body1" fontWeight={600} gutterBottom sx={{ color: '#DC2626' }}>
              {getUserFriendlyError(errorInfo)}
            </Typography>
            {errorInfo.code && errorInfo.code !== 'UNKNOWN_ERROR' && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Error Code: {errorInfo.code}
              </Typography>
            )}
          </Box>
          {onClose && (
            <IconButton size="small" onClick={onClose} sx={{ ml: 1 }}>
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box display="flex" gap={1} mt={2}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<BugReport sx={{ color: '#000000' }} />}
            onClick={() => setOpen(true)}
            sx={{
              bgcolor: '#F3F4F6',
              color: '#DC2626',
              borderColor: '#E5E7EB',
              '&:hover': {
                bgcolor: '#E5E7EB',
                borderColor: '#D1D5DB',
              },
            }}
          >
            Report Error
          </Button>
        </Box>
      </Box>

      {/* Report Dialog */}
      <Dialog open={open} onClose={() => !sending && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Report Error to Developer</DialogTitle>
        <DialogContent>
          {sent ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircle color="success" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6">Error Report Sent!</Typography>
              <Typography variant="body2" color="text.secondary">
                Thank you for helping us improve Elevair.
              </Typography>
            </Box>
          ) : (
            <>
              <DialogContentText>
                This will send error details to our development team to help us fix the issue.
                No personal information will be shared.
              </DialogContentText>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <div><strong>Error:</strong> {errorInfo.message}</div>
                <div><strong>Code:</strong> {errorInfo.code}</div>
                {errorInfo.endpoint && <div><strong>Endpoint:</strong> {errorInfo.endpoint}</div>}
                {errorInfo.method && <div><strong>Method:</strong> {errorInfo.method}</div>}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!sent && (
            <>
              <Button onClick={() => setOpen(false)} disabled={sending}>
                Cancel
              </Button>
              <Button
                onClick={handleReport}
                variant="contained"
                disabled={sending}
                startIcon={sending ? <CircularProgress size={16} /> : <BugReport />}
              >
                {sending ? 'Sending...' : 'Send Report'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
